import React, { useState, useEffect } from 'react';
import { replacePlaceholders, loadCoreData, getPrintSchedules, resolveScheduleTimes } from '../utils/dataLogic';
import { zmanLoader } from '../calendar/zmanim';
import { getDate, formatTime, parseTimeToDate, isInDisplayWindow } from '../utils/dateUtils';
import { renderScheduleSection } from '../utils/scheduleRenderer';

function PrintPage() {
  const [printConfig, setPrintConfig] = useState({ selectedSections: [] });
  const [scheduleData, setScheduleData] = useState(null);
  const [printSchedule, setPrintSchedule] = useState(null);
  const [announcements, setAnnouncements] = useState('');
  const [hebrewDate, setHebrewDate] = useState('');
  const [parsha, setParsha] = useState('');
  const [chazak, setChazak] = useState(false);
  const [specialDay, setSpecialDay] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [zmanimData, setZmanim] = useState([]);

  useEffect(() => {
    // Load print.css
    const printLink = document.createElement("link");
    printLink.rel = "stylesheet";
    printLink.href = "/print.css";
    printLink.media = "all";
    document.head.appendChild(printLink);
    
    // Add simple CSS
    const customStyle = document.createElement("style");
    customStyle.textContent = `
      @page {
        margin: 0.5in;
      }
      
      .print-schedule-container {
        margin: 0;
        padding: 0;
      }
      
      .print-header {
        margin: 0;
        padding: 10px;
        border-bottom: 1px solid #000;
        font-size: 0.9em;
      }
      
      .print-schedule {
        column-count: 2;
        column-gap: 20px;
        column-fill: balance;
      }
      
      .service-schedule {
        break-inside: avoid;
        page-break-inside: avoid;
        margin-bottom: 0;
        width: 100%;
      }
      
      .service-schedule h2 {
        font-size: 1.3em;
        font-weight: normal;
        text-align: right;
        text-decoration: underline;
        margin: 5px 0;
        color: black;
        display: block;
      }
      
      .schedule-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid #eee;
        padding: 2px 0;
        margin-bottom: 2px;
      }
      
      .schedule-title-container {
        flex: 1;
        margin-right: 10px;
        text-align: right;
      }
      
      .schedule-title {
        font-weight: bold;
        color: black;
        display: block;
      }
      
      .schedule-value {
        font-weight: normal;
        text-align: left;
        color: black;
      }
      
      /* Only apply LTR to time values, not Hebrew text */
      .schedule-value:not([data-hebrew="true"]) {
        direction: ltr;
        unicode-bidi: bidi-override;
      }
      
      .schedule-byline {
        font-size: 0.8em;
        color: #666;
        font-style: italic;
        margin-top: 1px;
        display: block;
      }
      
      /* Print-specific styles */
      @media print {
        .print-schedule {
          column-count: 2 !important;
          column-gap: 20px !important;
          column-fill: balance !important;
        }
        
        .service-schedule {
          break-inside: avoid !important;
          page-break-inside: avoid !important;
        }
      }
    `;
    document.head.appendChild(customStyle);
    
    return () => {
      if (document.head.contains(printLink)) {
        document.head.removeChild(printLink);
      }
      if (document.head.contains(customStyle)) {
        document.head.removeChild(customStyle);
      }
    };
  }, []);

  useEffect(() => {
    loadPrintData();
    
    // Set up core data
    const date = getDate();
    const coreData = loadCoreData(date);
    setHebrewDate(coreData.hebrewDate);
    setSpecialDay(coreData.specialDay);
    setParsha(coreData.parsha);
    setChazak(coreData.chazak);
    
    // Load zmanim
    const zmanimObj = zmanLoader(date);
    setZmanim(zmanimObj.zmanim);
  }, []);

  const loadPrintData = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('[PrintPage] loadPrintData started');

      // Try to load print configuration from API first
      try {
        console.log('[PrintPage] Fetching print config from /api/print-config');
        // Add cache-busting query parameter to force fresh fetch
        const cacheBuster = `?t=${Date.now()}`;
        const printConfigResponse = await fetch(`/api/print-config${cacheBuster}`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        
        console.log('[PrintPage] Print config response:', {
          ok: printConfigResponse.ok,
          status: printConfigResponse.status,
          statusText: printConfigResponse.statusText,
          url: printConfigResponse.url
        });
        
        if (printConfigResponse.ok) {
          const responseText = await printConfigResponse.text();
          console.log('[PrintPage] Print config response text:', responseText.substring(0, 200));
          
          let printConfigData;
          try {
            printConfigData = JSON.parse(responseText);
          } catch (e) {
            console.error('[PrintPage] Failed to parse print config response:', e, responseText);
            throw new Error('Failed to parse print config');
          }
          
          console.log('[PrintPage] Print config loaded from API:', {
            printConfigData,
            selectedSectionsCount: printConfigData.selectedSections?.length,
            hasSelectedSections: !!printConfigData.selectedSections,
            firstSectionKey: printConfigData.selectedSections?.[0]?.key,
            responseKeys: Object.keys(printConfigData)
          });
          
          setPrintConfig(printConfigData);
        } else {
          throw new Error('API not available');
        }
      } catch (apiError) {
        console.log('[PrintPage] API not available, trying localStorage:', apiError);
        // Fallback to localStorage
        const storedPrintConfig = localStorage.getItem('printConfig');
        if (storedPrintConfig) {
          const printConfigData = JSON.parse(storedPrintConfig);
          console.log('[PrintPage] Print config loaded from localStorage:', printConfigData);
          setPrintConfig(printConfigData);
        } else {
          
          console.log('[PrintPage] No print config found, using empty');
          setPrintConfig({ selectedSections: [] });
        }
      }

      // Load schedule data from local files
      console.log('[PrintPage] Loading schedule.json...');
      const scheduleResponse = await fetch('/schedule.json');
      const scheduleData = await scheduleResponse.json();
      console.log('[PrintPage] Schedule data loaded:', {
        hasDatedSchedules: !!scheduleData.datedSchedules,
        datedSchedulesCount: Object.keys(scheduleData.datedSchedules || {}).length,
        datedScheduleKeys: Object.keys(scheduleData.datedSchedules || {}).slice(0, 10)
      });
      setScheduleData(scheduleData);

      // Create print schedule using the same method as kiosk page
      const currentDate = getDate();
      console.log('[PrintPage] Loading zmanim for date:', currentDate);
      const zmanimObj = zmanLoader(currentDate);
      console.log('[PrintPage] Zmanim loaded:', {
        zmanimCount: zmanimObj.zmanim?.length,
        hasGetZman: typeof zmanimObj.getZman === 'function'
      });
      const printSchedule = getPrintSchedules(currentDate, scheduleData, zmanimObj);
      console.log('[PrintPage] Print schedule created:', {
        hasFullWeeklySchedules: !!printSchedule.fullWeeklySchedules,
        weeklyScheduleKeys: Object.keys(printSchedule.fullWeeklySchedules || {})
      });
      setPrintSchedule(printSchedule);

      // Set additional data
      setAnnouncements(scheduleData.announcements || '');
      console.log('[PrintPage] loadPrintData completed successfully');

    } catch (err) {
      console.error('[PrintPage] Error loading print data:', err);
      setError('Failed to load print data');
    } finally {
      setLoading(false);
    }
  };

  // Use existing utilities for consistent formatting

  const renderPrintSection = (sectionKey, sectionType) => {
    console.log('[PrintPage] renderPrintSection called:', { sectionKey, sectionType });
    
    // Use the resolved print schedule data like the kiosk page
    let section = null;
    
    if (sectionType === 'weekly' && printSchedule?.fullWeeklySchedules) {
      // Use the section that was selected in the admin
      section = printSchedule.fullWeeklySchedules[sectionKey];
      console.log('[PrintPage] Weekly section found:', { sectionKey, hasSection: !!section, scheduleLength: section?.schedule?.length });
      
    } else if (sectionType === 'dated' && scheduleData?.datedSchedules) {
      console.log('[PrintPage] Looking for dated section:', { 
        sectionKey, 
        hasDatedSchedules: !!scheduleData.datedSchedules,
        availableKeys: Object.keys(scheduleData.datedSchedules || {})
      });
      
      section = scheduleData.datedSchedules[sectionKey];
      console.log('[PrintPage] Dated section lookup result:', { 
        sectionKey, 
        found: !!section,
        hasSchedule: !!section?.schedule,
        scheduleLength: section?.schedule?.length,
        sectionTitle: section?.sectionTitle,
        displayDateTime: section?.displayDateTime,
        expirationDateTime: section?.expirationDateTime
      });
      
      // For dated sections, check if they exist
      // NOTE: For print page, we don't filter by display window - we show all configured sections
      // The display window check is for the kiosk page to show only active schedules
      if (section) {
        console.log('[PrintPage] Dated section found (skipping display window check for print):', {
          sectionKey,
          displayDateTime: section.displayDateTime,
          expirationDateTime: section.expirationDateTime,
          currentDate: getDate()
        });
        
        // For dated sections, don't resolve times here because items may have different dates
        // renderScheduleSection will handle item-specific date resolution
        // We just need to make a copy to avoid mutating the original
        section = { ...section, schedule: [...(section.schedule || [])] };
        console.log('[PrintPage] Section prepared for rendering:', {
          sectionKey,
          scheduleLength: section.schedule.length,
          firstItem: section.schedule[0]
        });
      }
    }
    
    if (!section || !section.schedule || section.schedule.length === 0) {
      console.log('[PrintPage] Section missing or empty:', {
        sectionKey,
        sectionType,
        hasSection: !!section,
        hasSchedule: !!section?.schedule,
        scheduleLength: section?.schedule?.length
      });
      return null;
    }

    console.log('[PrintPage] Calling renderScheduleSection:', {
      sectionKey,
      sectionType,
      sectionTitle: section.sectionTitle,
      scheduleLength: section.schedule.length,
      zmanimDataLength: zmanimData?.length
    });

    // Use the centralized rendering logic
    const rendered = renderScheduleSection(section, sectionType, zmanimData, getDate());
    console.log('[PrintPage] renderScheduleSection returned:', {
      sectionKey,
      rendered: !!rendered,
      renderedType: rendered?.type
    });
    
    return rendered;
  };

  const renderAnnouncements = (htmlContent) => {
    return <div dangerouslySetInnerHTML={{ __html: htmlContent }} />;
  };

  if (loading) {
    return (
      <div className="print-schedule-container">
        <div className="loading">Loading print data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="print-schedule-container">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="print-schedule-container">
      <div className="top-right-header">בס"ד</div>
      <div className="print-header">
        <div className="branding">
          <img src="/logo.png" alt="BLY Logo" /> Beis Levi Yitzchok
        </div>
        <div className="parsha">{parsha}{chazak ? ' - חזק' : ''}</div>
      </div>

      <div className="print-schedule">
        {(() => {
          console.log('[PrintPage] Rendering sections:', {
            printConfig,
            printConfigKeys: Object.keys(printConfig || {}),
            selectedSectionsCount: printConfig?.selectedSections?.length,
            selectedSections: printConfig?.selectedSections,
            hasScheduleData: !!scheduleData,
            hasPrintSchedule: !!printSchedule,
            hasZmanimData: !!zmanimData,
            zmanimDataLength: zmanimData?.length
          });
          
          if (!printConfig || !printConfig.selectedSections || printConfig.selectedSections.length === 0) {
            console.warn('[PrintPage] No selected sections to render:', {
              printConfig,
              hasPrintConfig: !!printConfig,
              selectedSectionsLength: printConfig?.selectedSections?.length
            });
            return <div>No sections selected for printing</div>;
          }
          
          return printConfig.selectedSections.map((section, index) => {
            console.log('[PrintPage] Processing section in map:', { index, section });
            
            // Skip page break sections
            if (section.type === 'page-break') {
              return null;
            }
            
            // Render regular sections - CSS Grid will handle the column placement
            const rendered = renderPrintSection(section.key, section.type);
            console.log('[PrintPage] Section rendered:', { index, sectionKey: section.key, rendered: !!rendered });
            
            return (
              <div key={`${section.type}-${section.key}-${index}`}>
                {rendered}
              </div>
            );
          });
        })()}
      </div>

      {announcements && (
        <div className="print-footer">
          <section className="announcements">
            {renderAnnouncements(announcements)}
          </section>
        </div>
      )}
    </div>
  );
}

export default PrintPage;